import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import { WHATSAPP_URL } from '@/constants/whatsapp';
import { getOrganizationSchema, getWebPageSchema, SCHEMA_SITE_URL } from '@/constants/schema';

const SITE_URL = import.meta.env.VITE_SITE_URL as string;

const PAGE_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    getOrganizationSchema(),
    getWebPageSchema({
      path: '/lab-gloves',
      name: 'Vinyl Nitrile Latex Gloves Wholesale Oman | Food Grade & Lab Grade | omandisposables.com',
      description: 'Wholesale vinyl, nitrile & latex gloves for food grade and lab use in Oman & GCC. Trusted supply track record.',
      breadcrumbId: `${SCHEMA_SITE_URL}/lab-gloves#breadcrumb`,
    }),
    {
      '@type': 'BreadcrumbList',
      '@id': `${SCHEMA_SITE_URL}/lab-gloves#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Vinyl, Nitrile & Latex Gloves', item: `${SITE_URL}/lab-gloves` },
      ],
    },
    {
      '@type': 'Product',
      '@id': `${SCHEMA_SITE_URL}/lab-gloves#product`,
      'name': 'Vinyl, Nitrile & Latex Gloves Wholesale Oman — Food & Lab Grade',
      'description': 'Wholesale vinyl, nitrile and latex disposable gloves for food grade and laboratory use in Oman and GCC. Powder-free and latex-free options available. Est. 1996.',
      'image': 'https://public.readdy.ai/ai/img_res/1259bad26a7b8abda5b729132f25f6b2.jpg',
      'brand': { '@type': 'Brand', 'name': 'Oman Disposables' },
      'category': 'Vinyl, Nitrile & Latex Gloves',
      'url': `${SCHEMA_SITE_URL}/lab-gloves`,
      'offers': {
        '@type': 'Offer',
        'priceCurrency': 'OMR',
        'availability': 'https://schema.org/InStock',
        'areaServed': ['OM', 'AE', 'SA', 'KW', 'BH', 'QA'],
        'seller': { '@type': 'Organization', 'name': 'Oman Disposables' },
      },
    },
    {
      '@type': 'ItemList',
      name: 'Vinyl, Nitrile & Latex Gloves — omandisposables.com',
      description: 'Wholesale vinyl, nitrile and latex gloves for food grade, lab grade and general-purpose use across Oman and GCC.',
      url: `${SITE_URL}/lab-gloves`,
      numberOfItems: 4,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@type': 'Product',
            name: 'Vinyl Gloves — Food Grade',
            description: 'Latex-free vinyl gloves ideal for food handling, catering and light-duty food processing.',
            image: 'https://readdy.ai/api/search-image?query=transparent%20clear%20vinyl%20disposable%20gloves%20on%20clean%20white%20kitchen%20counter%20beside%20fresh%20vegetables%20and%20food%20ingredients%2C%20professional%20food%20handling%20setting%2C%20bright%20natural%20lighting%2C%20commercial%20food%20safety%20photography&width=600&height=420&seq=vinyl-food-01&orientation=landscape',
            url: `${SITE_URL}/lab-gloves`,
            category: 'Vinyl Gloves',
            brand: { '@type': 'Brand', name: 'omandisposables.com' },
            offers: { '@type': 'Offer', seller: { '@type': 'Organization', name: 'omandisposables.com' }, availability: 'https://schema.org/InStock', priceCurrency: 'OMR', areaServed: ['OM', 'AE', 'SA', 'KW', 'BH', 'QA'] },
          },
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@type': 'Product',
            name: 'Nitrile Gloves — Food Grade',
            description: 'Premium food-safe nitrile gloves for butchers, food production and catering with EN455 certification.',
            image: 'https://readdy.ai/api/search-image?query=chef%20wearing%20black%20nitrile%20food%20safe%20disposable%20gloves%20preparing%20fresh%20meat%20and%20vegetables%20in%20professional%20commercial%20kitchen%2C%20stainless%20steel%20surfaces%2C%20bright%20clean%20culinary%20environment&width=600&height=420&seq=nitrile-food-01&orientation=landscape',
            url: `${SITE_URL}/lab-gloves`,
            category: 'Nitrile Gloves',
            brand: { '@type': 'Brand', name: 'omandisposables.com' },
            offers: { '@type': 'Offer', seller: { '@type': 'Organization', name: 'omandisposables.com' }, availability: 'https://schema.org/InStock', priceCurrency: 'OMR', areaServed: ['OM', 'AE', 'SA', 'KW', 'BH', 'QA'] },
          },
        },
        {
          '@type': 'ListItem',
          position: 3,
          item: {
            '@type': 'Product',
            name: 'Latex Gloves — Food Grade',
            description: 'Natural rubber latex gloves for food service workers. Excellent grip and flexibility.',
            image: 'https://readdy.ai/api/search-image?query=food%20service%20worker%20wearing%20natural%20beige%20latex%20gloves%20handling%20fresh%20bread%20and%20bakery%20products%20in%20clean%20commercial%20bakery%2C%20warm%20lighting%20and%20professional%20food%20production%20environment&width=600&height=420&seq=latex-food-01&orientation=landscape',
            url: `${SITE_URL}/lab-gloves`,
            category: 'Latex Gloves',
            brand: { '@type': 'Brand', name: 'omandisposables.com' },
            offers: { '@type': 'Offer', seller: { '@type': 'Organization', name: 'omandisposables.com' }, availability: 'https://schema.org/InStock', priceCurrency: 'OMR', areaServed: ['OM', 'AE', 'SA', 'KW', 'BH', 'QA'] },
          },
        },
        {
          '@type': 'ListItem',
          position: 4,
          item: {
            '@type': 'Product',
            name: 'Nitrile Gloves — Lab Grade',
            description: 'Powder-free nitrile gloves for laboratory and light chemical handling. EN374 rated.',
            image: 'https://readdy.ai/api/search-image?query=scientist%20wearing%20purple%20nitrile%20powder%20free%20laboratory%20gloves%20holding%20test%20tubes%20in%20modern%20research%20lab%20with%20clean%20white%20benches%20and%20scientific%20equipment%2C%20professional%20laboratory%20photography&width=600&height=420&seq=lab-nitrile-02&orientation=landscape',
            url: `${SITE_URL}/lab-gloves`,
            category: 'Lab Gloves',
            brand: { '@type': 'Brand', name: 'omandisposables.com' },
            offers: { '@type': 'Offer', seller: { '@type': 'Organization', name: 'omandisposables.com' }, availability: 'https://schema.org/InStock', priceCurrency: 'OMR', areaServed: ['OM', 'AE', 'SA', 'KW', 'BH', 'QA'] },
          },
        },
      ],
    },
  ],
};

const PRODUCTS = [
  {
    name: 'Vinyl Gloves — Food Grade',
    nameAr: 'قفازات فينيل — درجة غذائية',
    desc: 'Latex-free vinyl gloves ideal for food handling, catering and light-duty food processing. Comfortable fit with reliable barrier protection.',
    descAr: 'قفازات فينيل خالية من اللاتكس مثالية لتداول الأغذية والتموين والمعالجة الغذائية الخفيفة.',
    specs: ['Food Safe', 'Latex-Free', 'Powder-Free Available', 'Clear / Blue'],
    img: 'https://readdy.ai/api/search-image?query=transparent%20clear%20vinyl%20disposable%20gloves%20on%20clean%20white%20kitchen%20counter%20beside%20fresh%20vegetables%20and%20food%20ingredients%2C%20professional%20food%20handling%20setting%2C%20bright%20natural%20lighting%2C%20commercial%20food%20safety%20photography&width=600&height=420&seq=vinyl-food-01&orientation=landscape',
    tag: 'Food Safe',
    material: 'Vinyl',
    color: 'bg-brand-green/10 text-brand-green',
  },
  {
    name: 'Nitrile Gloves — Food Grade',
    nameAr: 'قفازات نيتريل — درجة غذائية',
    desc: 'Premium food-safe nitrile gloves for butchers, food production and catering. Superior puncture resistance and tactile sensitivity for food handling.',
    descAr: 'قفازات نيتريل فاخرة آمنة غذائياً للجزارين وإنتاج الأغذية والتموين. مقاومة ممتازة للثقب وحساسية لمسية.',
    specs: ['Food Safe', 'Puncture Resistant', 'Powder-Free', 'Black / Blue / Purple'],
    img: 'https://readdy.ai/api/search-image?query=chef%20wearing%20black%20nitrile%20food%20safe%20disposable%20gloves%20preparing%20fresh%20meat%20and%20vegetables%20in%20professional%20commercial%20kitchen%2C%20stainless%20steel%20surfaces%2C%20bright%20clean%20culinary%20environment&width=600&height=420&seq=nitrile-food-01&orientation=landscape',
    tag: 'Food Grade',
    material: 'Nitrile',
    color: 'bg-navy/10 text-navy',
  },
  {
    name: 'Latex Gloves — Food Grade',
    nameAr: 'قفازات لاتكس — درجة غذائية',
    desc: 'Natural rubber latex gloves for food service workers. Excellent grip and flexibility for handling fruits, bakery items and catering tasks.',
    descAr: 'قفازات لاتكس مطاط طبيعي لعمال خدمة الأغذية. قبضة ومرونة ممتازة للتعامل مع الفواكه والمخبوزات.',
    specs: ['Food Contact Safe', 'High Elasticity', 'Natural Rubber', 'Beige / White'],
    img: 'https://readdy.ai/api/search-image?query=food%20service%20worker%20wearing%20natural%20beige%20latex%20gloves%20handling%20fresh%20bread%20and%20bakery%20products%20in%20clean%20commercial%20bakery%2C%20warm%20lighting%20and%20professional%20food%20production%20environment&width=600&height=420&seq=latex-food-01&orientation=landscape',
    tag: 'Food Contact Safe',
    material: 'Latex',
    color: 'bg-yellow-500/10 text-yellow-700',
  },
  {
    name: 'Nitrile Gloves — Lab Grade',
    nameAr: 'قفازات نيتريل — درجة مختبرية',
    desc: 'Powder-free nitrile gloves for laboratory and light chemical handling. Chemical and puncture resistant with excellent tactile sensitivity.',
    descAr: 'قفازات نيتريل خالية من البودرة للمختبرات والتعامل الكيميائي الخفيف. مقاومة للثقب والمواد الكيميائية.',
    specs: ['Powder-Free', 'Chemical Resistant', 'Puncture Resistant', 'Blue / Violet'],
    img: 'https://readdy.ai/api/search-image?query=scientist%20wearing%20purple%20nitrile%20powder%20free%20laboratory%20gloves%20holding%20test%20tubes%20in%20modern%20research%20lab%20with%20clean%20white%20benches%20and%20scientific%20equipment%2C%20professional%20laboratory%20photography&width=600&height=420&seq=lab-nitrile-02&orientation=landscape',
    tag: 'Lab Grade',
    material: 'Nitrile',
    color: 'bg-navy/10 text-navy',
  },
];

const FEATURES = [
  { icon: 'ri-restaurant-line', en: 'Food Safe Options', ar: 'خيارات آمنة للأغذية' },
  { icon: 'ri-leaf-line', en: 'Latex-Free Options', ar: 'خيارات خالية من اللاتكس' },
  { icon: 'ri-drop-line', en: 'Powder-Free Available', ar: 'متوفر بدون بودرة' },
  { icon: 'ri-flask-line', en: 'Chemical Resistant', ar: 'مقاومة للمواد الكيميائية' },
  { icon: 'ri-award-line', en: 'Puncture Resistant', ar: 'مقاومة للثقب' },
  { icon: 'ri-stack-line', en: 'Bulk Wholesale Supply', ar: 'توريد بالجملة' },
];

export default function LabGlovesPage() {
  return (
    <main>
      <title>Vinyl Nitrile Latex Gloves Wholesale Oman | Food Grade & Lab Grade | omandisposables.com</title>
      <meta name="description" content="Wholesale vinyl, nitrile & latex gloves for food grade and lab use in Oman & GCC. Trusted supply. Bulk pricing via WhatsApp." />
      <meta name="keywords" content="vinyl gloves Oman, nitrile gloves wholesale Muscat, latex gloves GCC, lab gloves supplier, food grade vinyl nitrile latex" />
      <link rel="canonical" href={`${SITE_URL}/lab-gloves`} />
      <meta property="og:title" content="Vinyl Nitrile Latex Gloves Wholesale Oman | omandisposables.com" />
      <meta property="og:description" content="Wholesale vinyl, nitrile & latex gloves for food grade and lab use in Oman & GCC. Trusted supply." />
      <meta property="og:url" content={`${SITE_URL}/lab-gloves`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://readdy.ai/api/search-image?query=variety%20of%20disposable%20gloves%20vinyl%20nitrile%20latex%20arranged%20neatly%20on%20clean%20surface%20in%20professional%20wholesale%20distribution%20setting%2C%20bright%20lighting%20showing%20different%20glove%20types%20and%20colors%2C%20commercial%20product%20photography&width=1200&height=630&seq=lab-hero-og&orientation=landscape" />
      <meta property="og:site_name" content="omandisposables.com" />
      <meta property="og:locale" content="en_US" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Vinyl Nitrile Latex Gloves Wholesale Oman | omandisposables.com" />
      <meta name="twitter:description" content="Wholesale vinyl, nitrile & latex gloves food grade and lab grade for Oman & GCC." />
      <meta name="twitter:image" content="https://readdy.ai/api/search-image?query=variety%20of%20disposable%20gloves%20vinyl%20nitrile%20latex%20arranged%20neatly%20on%20clean%20surface%20in%20professional%20wholesale%20distribution%20setting%2C%20bright%20lighting%20showing%20different%20glove%20types%20and%20colors%2C%20commercial%20product%20photography&width=1200&height=630&seq=lab-hero-og&orientation=landscape" />
      <meta name="twitter:site" content="@omandisposables" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(PAGE_SCHEMA) }}
      />

      <Navbar />

      {/* Hero */}
      <section className="relative pt-24 pb-16 bg-navy overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=variety%20of%20disposable%20gloves%20vinyl%20nitrile%20latex%20arranged%20neatly%20on%20clean%20surface%20in%20professional%20wholesale%20distribution%20setting%2C%20bright%20lighting%20showing%20different%20glove%20types%20and%20colors%2C%20commercial%20product%20photography&width=1920&height=600&seq=gloves-hero-02&orientation=landscape"
            alt="Vinyl Nitrile Latex Gloves Wholesale Oman - omandisposables.com"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/90 to-navy/60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <div className="inline-flex items-center gap-2 bg-brand-green/20 border border-brand-green/40 text-brand-green text-sm font-semibold px-4 py-2 rounded-full mb-6">
            🏅 Trusted Vinyl, Nitrile & Latex Gloves Supplier from Oman
          </div>
          <h1 className="font-heading font-black text-white text-4xl md:text-5xl lg:text-6xl mb-3">
            Vinyl, Nitrile & Latex Gloves Wholesale
          </h1>
          <p className="font-arabic text-white/80 text-2xl md:text-3xl font-bold mb-4" dir="rtl">
            قفازات فينيل ونيتريل ولاتكس بالجملة
          </p>
          <p className="text-white/75 text-base md:text-lg max-w-2xl mb-4">
            Food grade, lab grade and general-purpose vinyl, nitrile &amp; latex gloves — wholesale supply
            across Oman and GCC. Bulk ready.
          </p>
          <p className="font-arabic text-white/65 text-base max-w-2xl mb-8 text-right" dir="rtl">
            قفازات فينيل ونيتريل ولاتكس للاستخدام الغذائي والمختبري والعام — توريد بالجملة عبر عُمان والخليج.
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp text-base px-8 py-4 rounded-full shadow-xl hover:scale-105 transition-transform inline-flex"
          >
            <i className="ri-whatsapp-line text-xl" />
            💬 WhatsApp for Wholesale Pricing
          </a>
        </div>
      </section>

      {/* Trust bar */}
      <div className="bg-brand-green py-3 px-4">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-center gap-6 text-white text-sm font-semibold">
          {['Food Safe', 'Vinyl Available', 'Nitrile Available', 'Latex Available', 'Bulk Ready'].map((t) => (
            <span key={t} className="flex items-center gap-1">
              <i className="ri-checkbox-circle-fill" />{t}
            </span>
          ))}
        </div>
      </div>

      {/* Material type tabs */}
      <section className="bg-white border-b border-gray-100 py-5 px-4">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-center gap-3">
          {[
            { label: 'Vinyl', labelAr: 'فينيل', icon: 'ri-hand-coin-line', color: 'bg-brand-green/10 text-brand-green border-brand-green/30' },
            { label: 'Nitrile', labelAr: 'نيتريل', icon: 'ri-hand-heart-line', color: 'bg-navy/10 text-navy border-navy/30' },
            { label: 'Latex', labelAr: 'لاتكس', icon: 'ri-leaf-line', color: 'bg-yellow-500/10 text-yellow-700 border-yellow-400/30' },
          ].map((m) => (
            <div key={m.label} className={`flex items-center gap-2 px-5 py-2.5 rounded-full border font-heading font-semibold text-sm ${m.color}`}>
              <i className={`${m.icon} text-base`} />
              {m.label}
              <span className="font-arabic text-xs opacity-70" dir="rtl">({m.labelAr})</span>
            </div>
          ))}
        </div>
      </section>

      {/* Products */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-12">
            <span className="trust-badge mb-4 inline-flex">🏅 Trusted Across Oman & GCC.</span>
            <h2 className="section-title-en text-3xl md:text-4xl mb-2">Food Grade & Lab Grade Gloves</h2>
            <p className="section-title-ar text-2xl md:text-3xl" dir="rtl">قفازات درجة غذائية ودرجة مختبرية</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-product-shop>
            {PRODUCTS.map((p) => (
              <div key={p.name} className="bg-white border border-gray-100 rounded-xl overflow-hidden hover:border-brand-green/30 hover:shadow-md transition-all">
                <div className="w-full h-52 overflow-hidden relative">
                  <img src={p.img} alt={`${p.name} - omandisposables.com Oman`} className="w-full h-full object-cover object-top" />
                  <div className={`absolute top-3 right-3 text-xs font-bold px-3 py-1 rounded-full ${p.color} bg-white`}>
                    {p.material}
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <h3 className="font-heading font-bold text-navy text-lg">{p.name}</h3>
                      <p className="font-arabic text-gray-500 text-sm" dir="rtl">{p.nameAr}</p>
                    </div>
                    <span className="shrink-0 bg-brand-green/10 text-brand-green text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">{p.tag}</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-2">{p.desc}</p>
                  <p className="font-arabic text-gray-500 text-xs text-right mb-4 leading-relaxed" dir="rtl">{p.descAr}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {p.specs.map((s) => (
                      <span key={s} className="text-xs bg-gray-50 border border-gray-200 text-gray-600 px-2.5 py-1 rounded-full">{s}</span>
                    ))}
                  </div>
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-whatsapp text-sm px-4 py-2.5 rounded-full w-full justify-center"
                  >
                    <i className="ri-whatsapp-line" />
                    Get Price & Availability
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features / Quality */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <div className="text-center mb-10">
            <h2 className="section-title-en text-2xl md:text-3xl mb-2">Quality Features</h2>
            <p className="section-title-ar text-xl md:text-2xl" dir="rtl">مميزات الجودة</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {FEATURES.map((f) => (
              <div key={f.en} className="flex flex-col items-center text-center p-5 bg-white rounded-xl border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-navy/5 mb-3">
                  <i className={`${f.icon} text-2xl text-navy`} />
                </div>
                <p className="font-heading font-semibold text-navy text-xs">{f.en}</p>
                <p className="font-arabic text-gray-400 text-xs mt-0.5" dir="rtl">{f.ar}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-navy text-center">
        <div className="container-max">
          <div className="inline-flex items-center gap-2 bg-brand-green/20 border border-brand-green/40 text-brand-green text-sm font-semibold px-4 py-2 rounded-full mb-6">
            🏅 جودة موثوقة. راسخون في عُمان. | Quality. Rooted in Oman.
          </div>
          <h2 className="font-heading font-black text-white text-3xl md:text-4xl mb-3">
            Order Vinyl, Nitrile & Latex Gloves Wholesale
          </h2>
          <p className="font-arabic text-white/80 text-xl font-bold mb-6" dir="rtl">
            طلب قفازات فينيل ونيتريل ولاتكس بالجملة
          </p>
          <p className="text-white/70 text-base max-w-lg mx-auto mb-8">
            Food grade, lab grade or general-purpose — get bulk pricing across all materials via WhatsApp. 24-hour response guaranteed.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp text-base px-8 py-4 rounded-full shadow-xl hover:scale-105 transition-transform w-full sm:w-auto justify-center"
            >
              <i className="ri-whatsapp-line text-xl" />
              💬 WhatsApp for Wholesale Price
            </a>
            <Link
              to="/request-quote"
              className="bg-white/10 border border-white/20 text-white font-heading font-semibold px-8 py-4 rounded-full hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer w-full sm:w-auto flex items-center justify-center gap-2"
            >
              📋 Request a Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}
